// FILE: src/pages/admin/student/AdminStudentLayout.jsx
// One account's pages for staff (WP-7; B-21: fetched by id, never taken from
// the list; MTO 11.1, matrix 1.13). The header is shared: name (the page's
// <h1>), statuses as icon + words, registration number, Edit and Lock /
// Unlock. Below it, sections as their own pages so nothing is crowded:
//   /admin/users/:id            Overview   identity, enrolment, account
//   /admin/users/:id/progress   Progress   lessons, credited time, scores
//   /admin/users/:id/activity   Activity   this account's audit log
//   /admin/users/:id/support    Support & survey
//   /admin/users/:id/records    Records    PDF / CSV / JSON with SHA-256
// GET /api/admin/users/:id → safe view + progressStatus, statuses,
// untimedHonoured, enrolment{…}, progressSummary{…}, survey{…}, supportTickets{…},
// staffRole{ id, name } (staff accounts).
// RBAC: sections and actions follow the signed-in staff member's role —
// Activity needs audit.view, Support & survey support.view, Records
// records.export; Edit and Lock / Unlock need students.edit / students.lock
// on a student and staff.manage on a staff account.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink, Outlet, useLocation, useParams } from "react-router-dom";
import { Alert, AlertTitle, Box, Button, Stack, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import fetchWithAuth from "../../../utils/fetchWithAuth";
import { EmptyState, ErrorState, LoadingState, PageHeader } from "../../../components/ui";
import SubNav from "../../../components/admin/SubNav";
import LockDialog from "../../../components/admin/LockDialog";
import { StudentStatusChips, lockReasonText } from "../../../components/admin/studentStatus";
import { formatDateTime, personName } from "../../../components/admin/format";
import { errorCode, messageFor } from "../../../i18n/errors";
import usePermissions from "../../../hooks/usePermissions";

const StudentContext = createContext(null);

/** { student, reload } for the section pages. */
export function useStudent() {
  return useContext(StudentContext);
}

export function studentSections(id, role, can = () => true) {
  const base = `/admin/users/${id}`;
  const all = [
    { label: "Overview", path: base },
    { label: "Progress", path: `${base}/progress`, studentOnly: true, need: "students.view" },
    { label: "Activity", path: `${base}/activity`, need: "audit.view" },
    { label: "Support & survey", path: `${base}/support`, studentOnly: true, need: "support.view" },
    { label: "Records", path: `${base}/records`, studentOnly: true, need: "records.export" }
  ];
  return all.filter((s) => !(role === "admin" && s.studentOnly) && (!s.need || can(s.need)));
}

export default function AdminStudentLayout() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const me = useSelector((state) => state.auth.user?._id);
  const { can } = usePermissions();
  const [state, setState] = useState({ student: null, loading: true, error: null });
  const [dialog, setDialog] = useState(null); // "lock" | "unlock" | null
  const [notice, setNotice] = useState(null);
  const latest = useRef(0);

  const reload = useCallback(() => {
    const req = ++latest.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    return fetchWithAuth(`/api/admin/users/${encodeURIComponent(id)}`)
      .then((student) => {
        if (req === latest.current) setState({ student, loading: false, error: null });
      })
      .catch((error) => {
        if (req === latest.current) setState((s) => ({ ...s, loading: false, error: error || "INTERNAL_ERROR" }));
      });
  }, [id]);

  useEffect(() => {
    setState({ student: null, loading: true, error: null });
    setNotice(null);
    reload();
  }, [reload]);

  const { student, error } = state;
  const breadcrumbs = (label) => [{ label: "Students", to: "/admin/users" }, { label }];
  const listCrumb = (account) =>
    account?.role === "admin" ? { label: "Staff", to: "/admin/staff" } : { label: "Students", to: "/admin/users" };

  if (!student) {
    if (error) {
      const missing = ["USER_NOT_FOUND", "NOT_FOUND"].includes(errorCode(error));
      return (
        <Box>
          <PageHeader title={missing ? "Account not found" : "Student"} breadcrumbs={breadcrumbs("Student")} />
          {missing ? (
            <EmptyState
              icon={PersonSearchOutlinedIcon}
              title={messageFor("USER_NOT_FOUND")}
              description="The link may be old, or the account was removed. Find the student in the list instead."
              action={
                <Button component={RouterLink} to="/admin/users" variant="contained">
                  Back to students
                </Button>
              }
            />
          ) : (
            <ErrorState error={error} title="We couldn't load this account" onRetry={reload} />
          )}
        </Box>
      );
    }
    return <LoadingState variant="page" label="Loading the account…" />;
  }

  const staff = student.role === "admin";
  const name = personName(student, staff ? "Staff member" : "Student");
  const isSelf = me && String(me) === String(student._id);
  const sections = studentSections(student._id, student.role, can);
  // Staff accounts are staff management; students need the student permission.
  const canEdit = can(staff ? "staff.manage" : "students.edit");
  const canLock = can(staff ? "staff.manage" : "students.lock");
  const current = sections.find((s) => s.path === pathname.replace(/\/$/, "")) || sections[0];

  const done = (result, { alreadyLocked }) => {
    if (alreadyLocked) {
      reload();
      return;
    }
    const locked = dialog === "lock";
    setDialog(null);
    setNotice(
      locked
        ? `${name}'s account is locked. They've been signed out and can't sign in until support unlocks it.`
        : `${name}'s account is unlocked. They can sign in again.`
    );
    if (result?.user) setState((s) => ({ ...s, student: { ...s.student, ...result.user } }));
    reload();
  };

  const lockButton = isSelf || !canLock ? null : student.accountLocked ? (
    <Button variant="contained" startIcon={<LockOpenOutlinedIcon />} onClick={() => setDialog("unlock")}>
      Unlock account
    </Button>
  ) : (
    <Button variant="outlined" color="error" startIcon={<LockOutlinedIcon />} onClick={() => setDialog("lock")}>
      Lock account
    </Button>
  );

  return (
    <StudentContext.Provider value={{ student, reload, isSelf }}>
      <Box>
        <PageHeader
          title={name}
          eyebrow={staff ? "Staff account" : "Student"}
          breadcrumbs={[listCrumb(student), { label: name, to: sections[0].path }, ...(current !== sections[0] ? [{ label: current.label }] : [])]}
          actions={
            canEdit || lockButton ? (
              <>
                {canEdit && (
                  <Button
                    component={RouterLink}
                    to={`/admin/users/${student._id}/edit`}
                    variant="outlined"
                    startIcon={<EditOutlinedIcon />}
                  >
                    Edit details
                  </Button>
                )}
                {lockButton}
              </>
            ) : undefined
          }
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1.5, sm: 3 }} sx={{ mt: 2, alignItems: { sm: "center" } }}>
            <StudentStatusChips user={student} />
            <Typography sx={{ color: "text.secondary", overflowWrap: "anywhere" }}>
              {student.email}
              {student.registrationNumber ? ` · Registration number ${student.registrationNumber}` : ""}
              {staff && student.staffRole?.name ? ` · Role: ${student.staffRole.name}` : ""}
            </Typography>
          </Stack>
        </PageHeader>

        {notice && (
          <Alert severity="success" role="status" sx={{ mb: 2.5 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}
        {student.accountLocked && (
          <Alert
            severity="warning"
            icon={<LockOutlinedIcon />}
            sx={{ mb: 2.5 }}
            data-testid="locked-banner"
          >
            <AlertTitle>This account is locked</AlertTitle>
            {(student.lockedAt || student.lockReason) && (
              <Typography sx={{ overflowWrap: "anywhere" }}>
                {[
                  student.lockedAt && `Since ${formatDateTime(student.lockedAt)}`,
                  student.lockReason && `Reason: ${lockReasonText(student.lockReason)}`
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            )}
            <Typography>
              {isSelf ? "Ask another administrator to unlock it." : "Unlock it only after you've confirmed the person's identity."}
            </Typography>
          </Alert>
        )}

        <SubNav label={staff ? "Account sections" : "Student sections"} items={sections} current={current.path} />

        <Outlet />

        <LockDialog
          open={!!dialog}
          mode={dialog || "lock"}
          user={student}
          name={name}
          onClose={() => setDialog(null)}
          onDone={done}
        />
      </Box>
    </StudentContext.Provider>
  );
}
